import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import CriteriaParameterAutocomplete from '../graph/CriteriaParameterAutocomplete';

const CriteriaEditorPanel = ({
  open,
  isSmallScreen,
  editLabel,
  editDescription,
  editStatus,
  editIncludeCriteria,
  editExcludeCriteria,
  editParameterListName,
  criteriaOperators,
  parameterLists = [],
  parameterListsLoading = false,
  parameterListsError = false,
  listMembersById = {},
  listMembersErrorById = {},
  expandingListId = null,
  onClose,
  onSave,
  onChangeLabel,
  onChangeDescription,
  onChangeStatus,
  onAddCriterion,
  onUpdateCriterion,
  onDeleteCriterion,
  onLoadParameterLists,
  onInsertParameterList,
  onLoadParameterListMembers,
  onInsertParameterListItem,
}) => {
  const [listMenu, setListMenu] = useState(null);
  const [expandedListId, setExpandedListId] = useState(null);

  const openListMenu = (event, group) => {
    onLoadParameterLists?.();
    setListMenu({ group, anchorEl: event.currentTarget });
    setExpandedListId(null);
  };

  const closeListMenu = () => {
    setListMenu(null);
    setExpandedListId(null);
  };

  const selectParameterList = (group, list) => {
    onInsertParameterList?.(group, list);
    closeListMenu();
  };

  const selectParameterListItem = (group, list, parameter) => {
    onInsertParameterListItem?.(group, list, parameter);
    closeListMenu();
  };

  const toggleListExpansion = (event, list) => {
    event.stopPropagation();
    const nextExpandedId = expandedListId === list.id ? null : list.id;
    setExpandedListId(nextExpandedId);
    if (nextExpandedId !== null) {
      onLoadParameterListMembers?.(list);
    }
  };

  const renderCriteriaList = (group, items) => (
    <Stack spacing={1.25}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {group === 'include' ? 'Include Criteria' : 'Exclude Criteria'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="text"
            startIcon={<PlaylistAddIcon />}
            onClick={(e) => openListMenu(e, group)}
            disabled={parameterListsLoading || !onInsertParameterList}
          >
            Insert List
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => onAddCriterion(group)}
          >
            {group === 'include' ? 'Add Include' : 'Add Exclude'}
          </Button>
        </Stack>
      </Stack>
      <Menu
        anchorEl={listMenu?.group === group ? listMenu.anchorEl : null}
        open={Boolean(listMenu?.group === group && listMenu.anchorEl)}
        onClose={closeListMenu}
      >
        {parameterListsLoading ? (
          <MenuItem disabled>Loading parameter lists…</MenuItem>
        ) : parameterListsError ? (
          <MenuItem disabled>Failed to load parameter lists. Reopen this menu to retry.</MenuItem>
        ) : parameterLists.length === 0 ? (
          <MenuItem disabled>No parameter lists available.</MenuItem>
        ) : (
          parameterLists.flatMap((list) => {
            const isExpanded = expandedListId === list.id;
            const items = [
              <MenuItem key={list.id} onClick={() => selectParameterList(group, list)}>
                <ListItemText primary={`${list.name} (${(list.parameterIds || []).length})`} />
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={`expand ${list.name}`}
                  onClick={(e) => toggleListExpansion(e, list)}
                >
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </MenuItem>,
            ];

            if (!isExpanded) return items;

            const members = listMembersById[list.id] || [];
            if (expandingListId === list.id) {
              items.push(
                <MenuItem key={`${list.id}-loading`} disabled sx={{ pl: 4 }}>
                  Loading parameters…
                </MenuItem>,
              );
            } else if (listMembersErrorById[list.id]) {
              // Review Runde 2, B5: ein API-Fehler ist ein anderer Zustand als
              // eine tatsaechlich leere Liste -- "No parameters" waere hier
              // eine falsche fachliche Aussage.
              items.push(
                <MenuItem key={`${list.id}-error`} disabled sx={{ pl: 4 }}>
                  Failed to load parameters for this list.
                </MenuItem>,
              );
            } else if (members.length === 0) {
              items.push(
                <MenuItem key={`${list.id}-empty`} disabled sx={{ pl: 4 }}>
                  No parameters in this list.
                </MenuItem>,
              );
            } else {
              members.forEach((parameter) => {
                items.push(
                  <MenuItem
                    key={`${list.id}-${parameter.id}`}
                    sx={{ pl: 4 }}
                    onClick={() => selectParameterListItem(group, list, parameter)}
                  >
                    {parameter.omopStatus !== 'validated' ? (
                      <WarningAmberIcon
                        color="warning"
                        fontSize="small"
                        sx={{ mr: 1 }}
                        aria-label={`${parameter.name} not executable`}
                      />
                    ) : null}
                    {parameter.name}
                  </MenuItem>,
                );
              });
            }

            return items;
          })
        )}
      </Menu>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {group === 'include' ? 'No include criteria defined yet.' : 'No exclude criteria defined yet.'}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((criterion) => (
            <Stack
              key={criterion.id}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              {criterion.executable === false ? (
                <Tooltip title="No OMOP mapping for this parameter -- not executable yet.">
                  <WarningAmberIcon color="warning" fontSize="small" aria-label={`${group} criterion not executable`} />
                </Tooltip>
              ) : null}
              <CriteriaParameterAutocomplete
                label="Field"
                value={criterion.field}
                onChange={(nextValue) => onUpdateCriterion(group, criterion.id, 'field', nextValue)}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id={`${group}-operator-${criterion.id}`}>Operator</InputLabel>
                <Select
                  labelId={`${group}-operator-${criterion.id}`}
                  value={criterion.operator}
                  label="Operator"
                  onChange={(e) => onUpdateCriterion(group, criterion.id, 'operator', e.target.value)}
                >
                  {criteriaOperators.map((operator) => (
                    <MenuItem key={operator.value} value={operator.value}>
                      {operator.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Value"
                size="small"
                value={criterion.value}
                onChange={(e) => onUpdateCriterion(group, criterion.id, 'value', e.target.value)}
                fullWidth
              />
              <IconButton
                size="small"
                color="error"
                aria-label={`delete ${group} criterion`}
                onClick={() => onDeleteCriterion(group, criterion.id)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
    >
      <DialogTitle>Edit Cohort Node</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Label"
            value={editLabel}
            onChange={(e) => onChangeLabel(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => onChangeDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Short explanation of this cohort node"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="cohort-node-status-label">Status</InputLabel>
            <Select
              labelId="cohort-node-status-label"
              value={editStatus}
              label="Status"
              onChange={(e) => onChangeStatus(e.target.value)}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="unedited">Unedited</MenuItem>
              <MenuItem value="edited">Edited</MenuItem>
              <MenuItem value="executing">Executing</MenuItem>
              <MenuItem value="executed">Executed</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>

          {renderCriteriaList('include', editIncludeCriteria)}
          {renderCriteriaList('exclude', editExcludeCriteria)}

          {editParameterListName ? (
            <Typography variant="caption" color="text.secondary">
              This node's criteria were last populated in part from parameter list "{editParameterListName}".
              Editing them here does not change the source list, and later changes to the list do not change
              these already-inserted criteria.
            </Typography>
          ) : null}

          <Typography variant="caption" color="text.secondary">
            Saving updates node label, description, status, and include/exclude criteria.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={!editLabel.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CriteriaEditorPanel;

